import {action, makeAutoObservable, observable} from "mobx";
import {inject, injectable} from "inversify";
import {isEmpty, reduce} from "lodash";
import {Bindings} from "data/constants/bindings";
import type {IJSONProvider} from "data/providers/json/json.provider";

export type IChecksums = Record<string, string>;

export interface IChecksumStore {
	get checksums(): IChecksums;
	get changedChecksums(): IChecksums;

	fetchChecksums(): Promise<void>;
}

@injectable()
export class ChecksumStore implements IChecksumStore {
	@observable protected _checksums: IChecksums = {};
	@observable _changedChecksums: IChecksums = {};

	get checksums() {
		return this._checksums;
	}

	get changedChecksums() {
		return this._changedChecksums;
	}

	constructor(@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider) {
		makeAutoObservable(this);
	}

	@action
	async fetchChecksums(): Promise<void> {
		try {
			const {data} = await this._jsonProvider.checksums();

			if (isEmpty(this.checksums)) {
				this._checksums = data;
			} else {
				this.compareChecksums(data);
			}
		} catch (e) {
			console.error("Error while fetching checksums");
		}
	}

	@action
	protected compareChecksums(checksums: IChecksums): void {
		this._changedChecksums = reduce(
			checksums,
			(acc, checksum, key) => {
				const oldChecksum = this._checksums[key];
				return checksum !== oldChecksum ? {...acc, [key]: checksum} : acc;
			},
			{}
		);

		this._checksums = checksums;
	}
}
